import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { LLMResultDefinition, LLMPromptTemplate } from "models/LLMs";
import React, { useState, useCallback } from "react";
import { LLMPromptTemplatesService } from "services/LLMPromptTemplatesService";

type LLMPromptTemplateSelectorProps = {
    llmResultDefinitions?: LLMResultDefinition[];
    getValue?: (options: LLMPromptTemplate[]) => LLMPromptTemplate | undefined;
} & Partial<Omit<React.ComponentPropsWithoutRef<typeof AcxSelectSingle<LLMPromptTemplate>>, "options">>;

export function LLMPromptTemplateSelector(
    props: LLMPromptTemplateSelectorProps,
) {
    const [options, setOptions] = useState<LLMPromptTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchOptions = useCallback(async () => {
        if (isLoading || !props.llmResultDefinitions) return;

        try {
            setIsLoading(true);
            const service = new LLMPromptTemplatesService();
            const templates = await service.findLLMPromptTemplates(
                props.llmResultDefinitions,
            );
            setOptions(templates);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, props.llmResultDefinitions]);

    return (
        <AcxSelectSingle
            id="llm-prompt-template"
            valueField="name"
            labelField="name"
            fullWidth
            {...props}
            defaultValue={props.getValue?.(options) ?? props.defaultValue}
            isLoading={isLoading}
            options={options}
            onMenuOpen={fetchOptions}
        />
    );
}

type AllLLMPromptTemplateSelectorProps = Partial<
    Omit<React.ComponentPropsWithoutRef<typeof AcxSelectSingle<LLMPromptTemplate>>, "options">
>;

export function AllLLMPromptTemplateSelector(
    props: AllLLMPromptTemplateSelectorProps,
) {
    const [options, setOptions] = useState<LLMPromptTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchOptions = useCallback(async () => {
        if (isLoading || options.length > 0) return;

        try {
            setIsLoading(true);
            const service = new LLMPromptTemplatesService();
            const templates = await service.getLLMPromptTemplates();
            setOptions(templates);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, options]);

    return (
        <AcxSelectSingle
            id="llm-prompt-template"
            valueField="name"
            labelField="name"
            fullWidth
            {...props}
            isLoading={isLoading}
            options={options}
            onMenuOpen={fetchOptions}
        />
    );
}
